/**
 * Datos de la ficha del documento (`/documento/:id`, rediseño AI Studio 15-10) sacados del resumen IA guardado en
 * `documents.resumen`. Medido en la BD el 15-10: las llaves cambian por tipo (oficio: criterio_aplicable,
 * alcance_practico, condiciones_excepciones, puntosRelevantes; circular: criterio_principal, ambito, instrucciones,
 * vigencia; resolución: objeto, sujetos, obligaciones_procedimiento, vigencia_plazos…; sentencias y leyes:
 * puntos_clave) y cada valor puede venir como texto, lista u objeto. Nada aquí inventa contenido: una sección sin dato
 * no se muestra.
 */
import { Document } from './supabase';
import { getSummaryText, parseAiSummary, stripJsonArtifacts } from './formatters';
import { numeroReal, tipoSingular, tituloDocumento } from './bibliotecas';
import { buildDocumentUrl, formatCitationDate, safeExternalUrl } from './citationFormat';

export interface Seccion {
  clave: string;
  titulo: string;
  items: string[];
}

/** Valores que el modelo escribió para decir «no hay» («N/A», «no aplica», «ninguna»). */
const SIN_DATO = /^(n\/?a|no aplica|ninguna?|ninguno|none|null|sin (excepciones|información|cambios?)( específicas)?|-|—)\.?$/i;

const humano = (k: string) => {
  const t = k.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** Texto, lista u objeto del resumen → lista de frases limpias (sin «N/A» ni vacíos). */
export function aLista(valor: unknown): string[] {
  if (valor == null) return [];
  if (typeof valor === 'number' || typeof valor === 'boolean') return [String(valor)];
  if (typeof valor === 'string') {
    const t = stripJsonArtifacts(valor).trim();
    if (!t || SIN_DATO.test(t)) return [];
    // Viñetas escritas en el texto («- 1) …; - 2) …») o saltos de línea: una frase por ítem.
    const partes = t.startsWith('- ') ? t.split(/(?:^|\s)-\s(?=\S)/) : t.split(/\n+/);
    return partes.map(p => p.trim()).filter(p => p && !SIN_DATO.test(p));
  }
  if (Array.isArray(valor)) return valor.flatMap(aLista);
  if (typeof valor === 'object') {
    return Object.entries(valor as Record<string, unknown>).flatMap(([k, v]) => {
      if (v && typeof v === 'object') return aLista(v);
      const lista = aLista(v);
      return lista.length ? [`${humano(k)}: ${lista.join('; ')}`] : [];
    });
  }
  return [];
}

type Def = [clave: string, titulo: string];

/** Tarjetas (lo principal, en dos columnas) y desplegables (el apoyo) por tipo de documento. */
const ESTRUCTURA: Record<string, { tarjetas: Def[]; desplegables: Def[] }> = {
  oficio: {
    tarjetas: [['criterio_aplicable', 'Criterio del SII'], ['alcance_practico', 'Alcance práctico']],
    desplegables: [['puntosRelevantes', 'Fundamentos y puntos relevantes'], ['condiciones_excepciones', 'Condiciones, límites y excepciones']],
  },
  circular: {
    tarjetas: [['criterio_principal', 'Criterio principal'], ['ambito', 'Ámbito']],
    desplegables: [['instrucciones', 'Instrucciones'], ['vigencia', 'Vigencia'], ['puntosRelevantes', 'Puntos relevantes']],
  },
  resolucion: {
    tarjetas: [['objeto', 'Objeto'], ['sujetos', 'A quién aplica']],
    desplegables: [
      ['obligaciones_procedimiento', 'Obligaciones y procedimiento'],
      ['vigencia_plazos', 'Vigencia y plazos'],
      ['sistemas_plataformas', 'Sistemas y plataformas'],
      ['derogaciones_modificaciones', 'Derogaciones y modificaciones'],
      ['anexos', 'Anexos'],
      ['puntosRelevantes', 'Puntos relevantes'],
    ],
  },
};
const GENERICA = { tarjetas: [] as Def[], desplegables: [['puntos_clave', 'Puntos clave'], ['puntosRelevantes', 'Puntos relevantes']] as Def[] };

export interface Ficha {
  /** Síntesis ejecutiva (texto limpio, nunca JSON). */
  sintesis: string;
  tarjetas: Seccion[];
  desplegables: Seccion[];
  /** Normas y doctrina que cita el documento (`referencias` del resumen). */
  referencias: string[];
  /** «Deja sin efecto Oficio N° 560 de 2003», si el resumen registra un cambio de criterio con documento afectado. */
  doctrinaPrevia: string | null;
  /** Materia descrita por el resumen (una frase), si la hay. */
  materia: string | null;
}

const ACCION: Record<string, string> = {
  deja_sin_efecto: 'Deja sin efecto',
  modifica: 'Modifica',
  complementa: 'Complementa',
  reinterpreta: 'Reinterpreta',
  sustituye: 'Sustituye',
  deroga: 'Deroga',
  confirma: 'Confirma',
  ratifica: 'Ratifica',
};

/** Documento afectado por un cambio de criterio, solo si trae tipo y número. */
function docAfectado(d: unknown): string | null {
  if (!d || typeof d !== 'object') return null;
  const { tipo, numero, anio } = d as { tipo?: unknown; numero?: unknown; anio?: unknown };
  const t = typeof tipo === 'string' ? tipo.trim() : '';
  const n = typeof numero === 'string' || typeof numero === 'number' ? String(numero).trim() : '';
  if (!t || !n) return null;
  const a = typeof anio === 'string' || typeof anio === 'number' ? String(anio).trim() : '';
  return `${t} N° ${n}${/^\d{4}$/.test(a) ? ` de ${a}` : ''}`;
}

export function doctrinaPrevia(cambio: unknown): string | null {
  if (!cambio || typeof cambio !== 'object') return null;
  const { existe, accion, doc_afectado } = cambio as { existe?: unknown; accion?: unknown; doc_afectado?: unknown };
  if (existe !== true) return null;
  const docs = (Array.isArray(doc_afectado) ? doc_afectado : [doc_afectado]).map(docAfectado).filter(Boolean) as string[];
  if (!docs.length) return null;
  const clave = typeof accion === 'string' ? accion.trim().toLowerCase().replace(/\s+/g, '_') : '';
  return `${ACCION[clave] || 'Se refiere a'} ${docs.join(', ')}`;
}

const normal = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

export function fichaDe(doc: Pick<Document, 'tipo' | 'resumen' | 'ai_summary'>): Ficha {
  const r = parseAiSummary(doc.ai_summary, doc.resumen);
  const tipo = (doc.tipo || '').toLowerCase();
  const sintesis = typeof r.resumen === 'string' && r.resumen.trim()
    ? stripJsonArtifacts(r.resumen).trim()
    : getSummaryText(r, doc.resumen, doc.tipo);
  const def = ESTRUCTURA[tipo === 'resolucion exenta' ? 'resolucion' : tipo] || GENERICA;
  const seccion = ([clave, titulo]: Def): Seccion => ({ clave, titulo, items: aLista(r[clave]) });
  // Una tarjeta igual a la síntesis (oficios sin `resumen`: la síntesis sale del criterio) no se repite.
  const tarjetas = def.tarjetas.map(seccion).filter(s => s.items.length && normal(s.items.join(' ')) !== normal(sintesis));
  const vistas = new Set<string>();
  const desplegables = def.desplegables.map(seccion).filter(s => {
    if (!s.items.length || vistas.has(s.clave)) return false;
    vistas.add(s.clave);
    return true;
  });
  const referencias = Array.from(new Set(aLista(r.referencias)));
  const materia = typeof r.materia === 'string' && r.materia.trim() ? stripJsonArtifacts(r.materia).trim() : null;
  return { sintesis, tarjetas, desplegables, referencias, doctrinaPrevia: doctrinaPrevia(r.cambio_criterio), materia };
}

/** «Oficio N° 2609», «RIT GR-16-00038-2022», «Convenio» (sin «N° 0»). */
export function etiquetaDocumento(doc: Pick<Document, 'institution_code' | 'tipo' | 'numero'>): string {
  const n = numeroReal(doc.numero);
  if (doc.institution_code?.toUpperCase() === 'TTA') return n ? `RIT ${n}` : 'Sentencia';
  return n ? `${tipoSingular(doc.tipo)} N° ${n}` : tipoSingular(doc.tipo);
}

export const tituloFicha = (doc: Pick<Document, 'ai_summary' | 'resumen' | 'tipo' | 'titulo'>) => tituloDocumento(doc) || doc.titulo || '';

/**
 * Cita para pegar en un escrito, con el formato de «Fuentes citadas» del chat (M-8) y el enlace a la ficha:
 * «SII - Oficio N° 2716 (09/10/2026): IVA de actividades de ProChile — https://…/documento/<id>».
 */
export function citaDocumento(doc: Pick<Document, 'id' | 'institution_code' | 'tipo' | 'numero' | 'year' | 'fecha_publicacion' | 'titulo' | 'resumen' | 'ai_summary'>, parrafo?: number | null): string {
  const inst = (doc.institution_code || '').toUpperCase();
  const n = numeroReal(doc.numero);
  const nombre = inst === 'TTA' ? `Sentencia${n ? ` RIT ${n}` : ''}` : `${tipoSingular(doc.tipo)}${n ? ` N° ${n}` : ''}`;
  const cuando = formatCitationDate(doc.fecha_publicacion) || (doc.year ? String(doc.year) : '');
  const titulo = tituloFicha(doc);
  // Con un párrafo activo (el de «#p-k», al abrir desde una cita) el enlace apunta a ese párrafo; el texto no lleva «¶» (dueño, 03-11).
  const p = parrafo && parrafo > 0 ? parrafo : null;
  return `${inst && inst !== 'BCN' ? `${inst} - ` : ''}${nombre}${cuando ? ` (${cuando})` : ''}${titulo ? `: ${titulo}` : ''} — ${buildDocumentUrl(doc.id)}${p ? `#p-${p}` : ''}`;
}

/** Minutos de lectura del texto íntegro (200 palabras por minuto), o 0 si no hay texto. */
export function minutosLectura(texto: string | null | undefined): number {
  const palabras = (texto || '').trim().split(/\s+/).filter(Boolean).length;
  return palabras ? Math.max(1, Math.round(palabras / 200)) : 0;
}

/**
 * URL del original que se puede mostrar dentro de la página: solo PDF por https desde R2 (los del SII). Las sentencias
 * de la OJV se descargan como adjunto y LeyChile y sii.cl prohíben mostrarse en un marco (X-Frame-Options, medido el
 * 15-10): esos se abren en su sitio.
 */
export function pdfEmbebible(url: string | null | undefined): string | null {
  const seguro = safeExternalUrl(url);
  if (!seguro) return null;
  const u = new URL(seguro);
  return u.protocol === 'https:' && u.hostname.endsWith('.r2.dev') && /\.pdf$/i.test(u.pathname) ? seguro : null;
}

/** Nombre del sitio de origen para «Abrir en …». */
export function sitioOrigen(url: string | null | undefined): string {
  const seguro = safeExternalUrl(url);
  if (!seguro) return '';
  const host = new URL(seguro).hostname.replace(/^www\./, '');
  if (host.endsWith('r2.dev')) return 'el archivo original';
  if (host === 'ojv.tta.cl') return 'la OJV del TTA';
  if (host === 'leychile.cl' || host === 'bcn.cl') return 'LeyChile';
  if (host === 'sii.cl') return 'sii.cl';
  return host;
}
